import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ButtonSubmit } from "@/components/blocks/button-submit";
import { useTranslation } from "@/providers/language-provider";

const formSchema = z.object({
  email: z
    .string()
    .email({ message: "Please enter a valid email address." })
    .min(5, { message: "Email must be at least 5 characters long." }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters long." })
    .max(32, { message: "Password must be no more than 32 characters long." }),
});

export function AuthLogin({
  onLogin,
  onGuestLogin = null,
}: {
  onLogin: any;
  onGuestLogin: any;
}) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { setValue } = form;

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    await onLogin(data.email, data.password);
    setIsSubmitting(false);
  }

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="w-full space-y-2"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Enter your e-mail."
                    autoComplete="email"
                    required
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter your password."
                    autoComplete="current-password"
                    required
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Password is 8~32 characters long.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="w-full space-y-2 pt-3">
            <ButtonSubmit
              className="w-full"
              type="submit"
              isSubmitting={isSubmitting}
            >
              {t("Login")}
            </ButtonSubmit>
          </div>
        </form>
      </Form>
      {onGuestLogin && (
        <Button
          variant="destructive"
          className="w-full"
          onClick={() => {
            // setValue("email", "test01@uzoo.in");
            // setValue("password", "xptmxmdkdlel");
            onGuestLogin(setValue);
          }}
        >
          Guest Login
        </Button>
      )}
    </>
  );
}
